export interface HeadingItem {
  id: string;
  text: string;
  level: number;
}

/**
 * Calculates estimated reading time in minutes based on average reading speed.
 */
export const calculateReadingTime = (htmlContent: string): number => {
  const text = htmlContent.replace(/<[^>]*>/g, ' '); // Strip HTML tags
  const wordCount = text.trim().split(/\s+/).length;
  const wordsPerMinute = 225; // Average reading speed
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return Math.max(1, minutes);
};

/**
 * Extracts h2 and h3 headings from the rendered article container and assigns IDs.
 */
export const extractHeadingsFromElement = (articleEl: Element): HeadingItem[] => {
  const headingElements = articleEl.querySelectorAll('h2, h3');
  const extracted: HeadingItem[] = [];

  headingElements.forEach((el, index) => {
    const text = el.textContent || '';
    const cleanSlug = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 40);

    const id = el.id || `${cleanSlug || 'heading'}-${index}`;
    el.id = id;
    el.classList.add('scroll-mt-24');

    extracted.push({
      id,
      text,
      level: el.tagName.toLowerCase() === 'h3' ? 3 : 2,
    });
  });

  return extracted;
};

/**
 * Generates Schema.org JSON-LD Structured Data for Article SEO
 */
export const generateBlogSchemaMarkup = (post: any) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    alternativeHeadline: post.subtitle || '',
    image: post.coverUrl || 'https://uibn.id/images/blog-default.jpg',
    genre: 'Edukasi Literasi',
    keywords: 'UIBN, ISBN, registrasi buku, penulis, penerbit',
    publisher: {
      '@type': 'Organization',
      name: 'Lembaga Registrasi UIBN Nasional',
      logo: 'https://uibn.id/logo.png',
    },
    url: typeof window !== 'undefined' ? window.location.href : '',
    datePublished: new Date(post.publishedAt || post.createdAt).toISOString().split('T')[0],
    dateCreated: new Date(post.createdAt).toISOString().split('T')[0],
    author: {
      '@type': 'Person',
      name: post.authorName,
      jobTitle: post.authorRole || 'Tim Redaksi UIBN',
    },
    description: post.summary || post.subtitle || 'Baca artikel menarik ini di Blog UIBN',
  };
};

/**
 * Handles Web Share API with clipboard fallback
 */
export const handleShareArticle = async (
  title?: string,
  text?: string,
  onNotify?: (title: string, desc: string, type: 'info' | 'success' | 'error') => void
) => {
  const shareData = {
    title: title || 'Blog UIBN',
    text: text || 'Baca artikel menarik ini di Blog UIBN',
    url: window.location.href,
  };

  try {
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      await navigator.clipboard.writeText(window.location.href);
      if (onNotify) {
        onNotify('Tautan Disalin', 'Tautan artikel telah disalin ke papan klip.', 'info');
      }
    }
  } catch (err: any) {
    if (err.name !== 'AbortError') {
      console.error('Error sharing:', err);
    }
  }
};
